import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import pluralize from 'pluralize'

import { Drawer } from '@/components/drawer/Drawer'
import Select from '@/components/forms/Select'
import { PageHeader } from '@/components/headers/PageHeader'
import { Loader } from '@/components/loaders/Loader'
import NoData from '@/components/NoData'
import { Pill } from '@/components/pills/Pill'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { useBackoffPoll } from '@/utils/hooks/useBackoffPoll'
import { getOrganizationsForBaltoOrHierarchy } from '@/utils/helpers'
import { fetchAssetExports, isTerminal } from '@/reducers/callAssetExports/callAssetExports.actions'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'
import { CallAssetExportDetail, STATUS_PILL } from '@/views/Calls/CallAssetExportDetail'

import './exportShell.scss'

// Starts slower than the job page: this is a browse view rather than one
// opened to watch a single job, and it eases off the same way.
const POLL_SCHEDULE = [
  { every: 15000, until: 120000 },
  { every: 30000, until: 300000 },
  { every: 60000, until: Infinity },
]

export const CallAssetExportsPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  // `/exports/:id` is this page with that export's drawer open. The id lives
  // in the path rather than the drawer's own `?id=` because the emails,
  // notifications and the toast all link here, and a logged-out visit
  // returns from login with the pathname only.
  const { id: openExportId } = useParams()
  const { exports, loading, exportsForbidden } = useSelector((state) => state.callAssetExports)
  const {
    organizationid: currentUserOrg,
    hierarchy_manager: isHierarchyManager,
    own_organization_id: ownOrganizationId,
  } = useSelector((state) => state.currentUser)
  const organizations = useSelector((state) => state.organizations)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  // Seeds the page, and nothing more. This used to be the only way the page
  // could land on another organization, which worked when it was reached from
  // Explorer and not at all once you arrive from the sidebar's own link: the
  // filter is then whatever Explorer was last left at, or empty. Worse, the
  // page said "your organization" either way, so a stale filter showed a
  // customer's exports under a heading claiming they were your own.
  const filterOrganizationId = useSelector((state) => state.callSearch?.filters?.organizationId)

  const isBaltoAdmin = currentUserOrg === 1
  const childOrgSelected = isHierarchyManager && ownOrganizationId !== currentUserOrg
  const { hasOrganizationDropdown, organizationOptions } = getOrganizationsForBaltoOrHierarchy({
    organizations,
    flattenedUserOrgHierarchy,
    isBaltoAdmin,
    isHierarchyManager,
    childOrgSelected,
  })

  const [organizationId, setOrganizationId] = useState(filterOrganizationId || currentUserOrg)

  useEffect(() => {
    if (hasOrganizationDropdown && isEmpty(organizations)) {
      dispatch(fetchOrganizations())
    }
  }, [hasOrganizationDropdown])

  useEffect(() => {
    dispatch(fetchAssetExports(organizationId))
  }, [organizationId])

  // Keyed on the organization, like the job page's own refusal: a refusal
  // for the organization the user just switched away from must not put the
  // empty state on the one they are looking at now.
  const forbidden = exportsForbidden != null && exportsForbidden === organizationId

  // Named rather than implied once the list can show someone else's exports.
  const selectedOrganizationName = organizationOptions.find(
    (option) => option.value === organizationId
  )?.label
  // Three cases, not two. Claiming "your organization" when it is not is the
  // thing this page was mislabelling in the first place, and the name is not
  // always available to say instead -- the options arrive a moment after the
  // page does, and a hierarchy manager's options need not contain an
  // organization seeded from a stale Explorer filter. So an unnamed
  // organization gets neutral copy rather than the wrong claim.
  let description
  if (organizationId === currentUserOrg) {
    description = 'Bulk downloads of call audio and transcripts, for everyone in your organization.'
  } else if (selectedOrganizationName) {
    description = `Bulk downloads of call audio and transcripts, for everyone in ${selectedOrganizationName}.`
  } else {
    description =
      'Bulk downloads of call audio and transcripts, for everyone in the selected organization.'
  }

  // Longer than the job page's own poll: this is a browse view, and an export
  // takes minutes, so a slower tick still shows a job settling without asking
  // the API for the whole list every few seconds.
  // Nothing left to learn once every row has reached a terminal state, so the
  // poll stops rather than running for as long as the tab is open.
  const unsettled = exports.some((row) => !isTerminal(row.status))

  useBackoffPoll(() => dispatch(fetchAssetExports(organizationId, { quiet: true })), {
    enabled: unsettled,
    schedule: POLL_SCHEDULE,
  })

  // Opened from a row, closing steps back to the list entry that row was
  // clicked on. Arriving any other way (an email, a notification, the toast)
  // replaces the entry instead, so Back from the list still returns to
  // wherever the link was followed from rather than reopening the drawer.
  const closeExport = () => {
    if (location.state?.fromList) {
      history.goBack()
    } else {
      history.replace('/exports')
    }
  }

  // Not while an export is open. On a cold deep link -- an email, a
  // notification, or the toast before the list has loaded -- the drawer
  // mounts first and starts fetching its export, and this return would
  // unmount it the moment the list's own fetch set `loading.exports`. It
  // then remounted and fetched again when the list arrived, which on a
  // forbidden link toasted the refusal twice.
  if (loading.exports && isEmpty(exports) && !openExportId) {
    return <Loader page />
  }

  const columns = [
    {
      label: 'Started',
      accessor: 'created_at',
      format: (value, row) => (
        <Link
          to={{ pathname: `/exports/${row.id}`, state: { fromList: true } }}
          className="table-link"
        >
          {new Date(value).toLocaleString()}
        </Link>
      ),
    },
    {
      label: 'Calls',
      accessor: 'call_count',
      format: (value) => value.toLocaleString(),
    },
    { label: 'Includes', accessor: 'asset_summary' },
    {
      label: 'Size',
      accessor: 'size_label',
      // Null until the job's HEAD sweep lands, which is seconds rather than
      // never -- so an em dash rather than a zero, which would read as an
      // empty archive.
      format: (value) => value || '—',
    },
    {
      label: 'Status',
      accessor: 'status',
      format: (value) => {
        const pill = STATUS_PILL[value] || { label: value }
        return (
          <Pill brand={pill.brand} success={pill.success} critical={pill.critical}>
            {pill.label}
          </Pill>
        )
      },
    },
  ]

  return (
    <div className="export-shell">
      <PageHeader
        title="Exports"
        description={description}
        actions={
          hasOrganizationDropdown && (
            <Select
              options={organizationOptions}
              value={organizationId}
              onChange={(option) => setOrganizationId(option.value)}
              placeholder="Select Organization"
              isClearable={false}
              fixedWidth
            />
          )
        }
      />

      {forbidden ? (
        // Distinct from "no exports yet", which would read as an answer
        // about this organization rather than a refusal to give one. Job
        // metadata is no longer withheld from Balto for an unredacted
        // organization, so reaching this means the account has no access to
        // the organization at all.
        <NoData
          headerText="You cannot see this organization's exports"
          text="Your account does not have access to this organization."
        />
      ) : loading.exports && isEmpty(exports) ? (
        // Only reachable with a drawer open, which is what skips the
        // full-page loader above. Without this the list behind it reads
        // "No exports yet" for an organization that has them.
        <Loader centered />
      ) : isEmpty(exports) ? (
        <NoData
          headerText="No exports yet"
          text="Select calls in Explorer and choose Download Call Assets to start one."
        />
      ) : (
        <AdvancedTable
          columns={columns}
          rows={exports}
          index="id"
          defaultOrderBy="created_at"
          defaultOrder="desc"
          loading={loading.exports}
        />
      )}

      {openExportId && (
        <Drawer key={openExportId} title="Export" autoTrigger syncUrl={false} onClose={closeExport}>
          {(handleClose) => <CallAssetExportDetail onClose={handleClose} />}
        </Drawer>
      )}

      <p className="muted-text">
        {loading.exports && isEmpty(exports)
          ? 'Exports stay available for a week.'
          : `Exports stay available for a week. ${exports.length} recent ${pluralize(
              'export',
              exports.length
            )}.`}
      </p>
    </div>
  )
}

export default CallAssetExportsPage
